import type { LatLon } from "@radar-sim/schema";
import { bearingDeg, distanceNm, projectPosition } from "../geodesy.js";
import type {
  BehaviourImpl,
  BehaviourPose,
  BehaviourRuntime,
} from "./types.js";

export interface FollowLaneParams {
  laneId: string;
}

const SECONDS_PER_HOUR = 3600;

/**
 * Vessel traverses a named lane (ordered list of lat/lon points) at its
 * spawn speed, holding cog along each segment. When the vessel reaches the
 * end of the lane, it dead-reckons on the final segment's bearing.
 */
export const FollowLane: BehaviourImpl<FollowLaneParams> = {
  name: "follow_lane",
  version: "1.0.0",
  init({ params, spawn, spawnSimSeconds }): BehaviourRuntime {
    return {
      poseAt(simSeconds, ctx) {
        const lane = ctx.areaData?.getLane(params.laneId);
        if (lane === undefined || lane.length === 0) {
          // No lane data: hold spawn pose. (Validation should have caught
          // this at load; this is the safe runtime fallback.)
          return { ...spawn };
        }
        const elapsed = Math.max(0, simSeconds - spawnSimSeconds);
        const distNmFromSpawn = spawn.sog * (elapsed / SECONDS_PER_HOUR);
        return advanceAlongLane(lane, spawn.position, distNmFromSpawn, spawn.sog);
      },
    };
  },
};

function advanceAlongLane(
  lane: readonly LatLon[],
  start: LatLon,
  distRemaining: number,
  sog: number,
): BehaviourPose {
  // Find the closest point on the lane to start; if start is itself a lane
  // node we begin from there. For simplicity we always start at the first
  // node and project total distance forward; spawn position is taken as the
  // vessel's launch point onto the lane.
  let segIdx = 0;
  let here: LatLon = start;
  let cog = lane.length > 1 ? bearingDeg(lane[0]!, lane[1]!) : 0;

  while (distRemaining > 0 && segIdx < lane.length - 1) {
    const next = lane[segIdx + 1]!;
    const segCog = bearingDeg(here, next);
    const segDist = distanceNm(here, next);
    if (segDist <= 0) {
      segIdx++;
      continue;
    }
    if (distRemaining <= segDist) {
      const finalPos = projectPosition(here, segCog, distRemaining);
      return { position: finalPos, cog: segCog, sog };
    }
    distRemaining -= segDist;
    here = next;
    segIdx++;
    cog = segCog;
  }

  if (distRemaining > 0) {
    // Past the end of the lane: dead-reckon at the final segment's bearing.
    here = projectPosition(here, cog, distRemaining);
  }
  return { position: here, cog, sog };
}
